import { Check, Download, QrCode, Share2, Wallet, Wifi } from 'lucide-react';
import { FramesGallery } from '../demos/FramesGallery';
import { ExchangeDemo } from '../demos/ExchangeDemo';
import { CtaLink, Headline, Kicker, Reveal, Section, Spec } from '../ui';
import { PublicPagePreview } from '../demos/BrandPreviews';
import { SECTION_TYPE_LIST } from '@/lib/sectionTypes';
import { IdentityStory, JourneyStory } from '../ScrollStory';

export function CardsPage() {

  return (
    <>
      <Section className="play-subhero play-subhero--cards pb-20 pt-16">
        <Reveal className="max-w-3xl">
          <Kicker>Cards · your portable corner of the internet</Kicker>
          <h1 className="play-subhero__title mt-4">
            A card you'd actually <em>want</em> to hand someone.
          </h1>
          <p className="play-subhero__body mt-6 max-w-xl">
            Most digital business cards look like signup forms. Haphe cards look like something a
            designer made for you: eight frames, custom palettes, your photo, your logo at the size
            you want, and a public page that holds everything behind the introduction.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="play-subhero__stage mt-14">
          <FramesGallery />
        </Reveal>
      </Section>

      {/* Exchange */}
      <IdentityStory />
      <JourneyStory kind="cards" />
      <Section className="play-page-section py-24">
        <div className="brand-exchange-layout">
          <Reveal>
            <Kicker>Save · Exchange</Kicker>
            <Headline className="mt-4">Two taps, both directions.</Headline>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink-muted">
              "Save to phone" downloads a vCard with your photo, title, company, phone, email,
              website, address, and every social you've added. "Exchange contact" asks for their
              name and email, optionally a photo, and sends their details to your inbox while
              handing them your card back. One motion, both sides hold the other's card.
            </p>
            <div className="brand-exchange-specs mt-6 grid gap-3">
              <Spec title="Save to phone" rows={[['format', 'vCard 3.0'], ['includes', 'photo, socials, links'], ['app needed', 'none']]} />
              <Spec title="Exchange" rows={[['collects', 'name, email, phone, note, photo'], ['lands in', 'your inbox + SMS'], ['returns', 'your vCard']]} />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="brand-exchange-stage">
            <ExchangeDemo dark={false} />
          </Reveal>
        </div>
      </Section>

      <Section className="brand-sharing py-24">
        <Kicker>How it gets to them</Kicker>
        <Headline className="mt-4">One card. Four ways to share.</Headline>
        <p className="mt-5 max-w-xl text-ink-muted">Choose what fits the moment. Every option opens the same public page, with no app for your visitor to install.</p>
        <div className="brand-sharing-grid">
          {[
            { icon: QrCode, name: 'Show your QR', body: 'They point their camera at your code. Your page opens.' },
            { icon: Share2, name: 'Send your link', body: 'Drop it into a message, email signature, or social bio.' },
            { icon: Wallet, name: 'Keep it in Wallet', body: 'Your Google Wallet pass keeps your QR close at hand.' },
            { icon: Wifi, name: 'Tap an NFC tag', body: 'Add your link to a compatible NFC card or tag. Tap to open.' },
          ].map(({ icon: Icon, name, body }) => <article key={name}><Icon/><h3>{name}</h3><p>{body}</p></article>)}
        </div>
      </Section>

      {/* Sections */}
      <Section className="play-page-section play-page-section--warm py-24"><div className="brand-preview-layout"><div>
        <Reveal className="max-w-2xl">
          <Kicker>The page beneath the card</Kicker>
          <Headline className="mt-4">Blocks you drag into the order you want.</Headline>
          <p className="mt-5 text-base leading-relaxed text-ink-muted">
            The public page is a stack of sections. Add the ones you need, rename them, drag them,
            and drag your About text and events among them. The editor previews live on desktop and
            in a full-screen sheet on your phone.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {SECTION_TYPE_LIST.map((s, i) => (
            <Reveal key={s.type} delay={i * 0.03}>
              <div className="play-block-row flex h-full gap-3 rounded-2xl border border-bone-deep bg-surface p-4">
                <s.icon className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <div>
                  <p className="text-sm font-medium tracking-tight">{s.label}</p>
                  <p className="mt-1 text-[12px] leading-snug text-ink-muted">{s.description}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 grid gap-3 sm:grid-cols-3">
          {[
            'Two pinned call-to-action buttons above Save / Exchange',
            'Company logo with a size slider, per card',
            'Cover photo, profile photo with crop, AI headshot from a selfie',
          ].map((t) => (
            <p key={t} className="flex items-start gap-2 text-sm text-ink-soft">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              {t}
            </p>
          ))}
        </Reveal>
        </div><PublicPagePreview /></div>
      </Section>

      {/* Starter kits */}
      <Section dark className="play-page-dark play-page-dark--blue py-24">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <Kicker gold>Ready-made templates</Kicker>
            <Headline className="mt-4 text-bone">Pick a starting point. Make it yours.</Headline>
            <p className="mt-5 text-base leading-relaxed text-bone/65">
              Choose a template for what you do: founder, realtor, podcaster, or maker. It gives
              you a design and useful page sections. Add your photo, replace the sample text, and
              your card is ready to share. You can change every part later.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Spec
              dark
              title="Three simple steps"
              rows={[
                ['01 · Choose', 'Pick a template for your role'],
                ['02 · Personalize', 'Add your photo, bio, and links'],
                ['03 · Share', 'Your QR and link are ready'],
              ]}
            />
          </Reveal>
        </div>
      </Section>

      <Section className="play-page-final py-24 text-center">
        <Reveal>
          <Headline size="xl" className="mx-auto max-w-3xl">
            Your first card takes a minute.
          </Headline>
          <div className="mt-8 flex justify-center gap-3">
            <CtaLink to="/signup">Get your card, free</CtaLink>
            <CtaLink to="/features/shop" variant="ghost">
              <Download className="h-4 w-4" /> Next: the shop
            </CtaLink>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
